import { cloneDeep } from 'lodash';
import { i18nKeyType } from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { EnumType } from '../../../../../util/enum';
import { QNear, Waypoint } from '../../../../../util/machine-variable.utils';
import {
  FineTunedPointRequired,
  MotionPoint,
} from '../../../../../util/part.util';
import { PickerOption } from '../../../shared/component/widget/widget.types';
import { MoveToPointProps } from '../../../shared/hook/move-to-point.hooks';
import {
  FineTuneProps,
  Flow,
  FlowItem,
  FlowItemArrayType,
  FlowItemMetadata,
  FlowItemParameter,
  FlowItemParameterMetadata,
  FlowItemSharedRef,
  FlowItemText,
  FlowSelectedItemInfo,
  SystemUnit,
} from '../../../shared/model/flow.types';
import { AbstractFlowItemService } from '../../../shared/service/abstract-flow-item.service';
import {
  ConvertMotionPointToFlowItemParams,
  ConvertMotionPointsToFlowItemSectionParams,
  ConvertMotionPointsToFlowItemSectionsParams,
} from '../../../shared/service/service.types';
import {
  incrementItemStats,
  instanceOfHelper,
} from '../../../shared/util/flow-item.util';
import {
  convertParameterValueToUOM,
  getParameterValue,
} from '../../../shared/util/flow-parameter.util';
import { getNextChar } from '../../../shared/util/string';
import { BeaconCuttingData } from '../model/beacon-cutting-data';
import { isCutType } from '../util/beacon-cutting.util';

class CuttingFlowItemServiceImpl extends AbstractFlowItemService<BeaconCuttingData.FlowConfig> {
  constructor(items: FlowItemMetadata[]) {
    super(items, BeaconCuttingData.FlowItemRef.CUT);
  }

  public canConvertFlowItemToMultipass(_item: FlowItem): boolean {
    // see welding-flow-item.service.ts when ready to implement
    return false;
  }

  public canConvertFlowItemToStitch(_item: FlowItem): boolean {
    // see welding-flow-item.service.ts when ready to implement
    return false;
  }

  public convertFlowItemsToMotionPoints(_items: FlowItem[]): MotionPoint[] {
    // see welding-flow-item.service.ts when ready to implement
    throw new Error('Method not implemented.');
  }

  public isMultipassSupportedMotionType(_item: FlowItem): boolean {
    // see welding-flow-item.service.ts when ready to implement
    throw new Error('Method not implemented');
  }

  public isStitchSupportedMotionType(_item: FlowItem): boolean {
    // see welding-flow-item.service.ts when ready to implement
    throw new Error('Method not implemented');
  }

  public convertMotionPointsToFlowItemSections(
    _params: ConvertMotionPointsToFlowItemSectionsParams
  ): FlowItem[] {
    // see welding-flow-item.service.ts when ready to implement
    throw new Error('Method not implemented.');
  }

  public convertMotionPointsToFlowItemSection(
    _params: ConvertMotionPointsToFlowItemSectionParams<EnumType>
  ): FlowItem[] {
    // see welding-flow-item.service.ts when ready to implement
    throw new Error('Method not implemented.');
  }

  public convertMotionPointToFlowItem(
    _params: ConvertMotionPointToFlowItemParams
  ): FlowItem {
    // see welding-flow-item.service.ts when ready to implement
    throw new Error('Method not implemented.');
  }

  public getRequiredParametersForTeaching(
    ref: string
  ): FlowItemParameterMetadata[] {
    const item = this.getItemMetadata(ref);
    const params: FlowItemParameterMetadata[] = [];
    if (ref === BeaconCuttingData.FlowItemRef.CUT) {
      const type = item?.availableParameters.find((p) => p.apiName === 'type');
      if (type) {
        params.push(type);
      }
    }
    return params;
  }

  public recalculateParameterValues(
    config: BeaconCuttingData.FlowConfig,
    items: FlowItem[],
    options?: {
      isDefaultParameterSet?: boolean;
    }
  ): FlowItem[] {
    const { isDefaultParameterSet = false } = options ?? {};
    let contiguousCuts = 0;
    let itemIdx = 0;

    for (const item of items) {
      const itemMetadata = this.getItemMetadata(item.ref);

      if (item.ref !== BeaconCuttingData.FlowItemRef.SECTION && itemMetadata) {
        const singleItemCut = this.isSinglePrimaryActionItem(item);

        if (item.ref === BeaconCuttingData.FlowItemRef.CUT && !singleItemCut) {
          contiguousCuts++;
        } else {
          contiguousCuts = 0;
        }

        for (const parameter of item.parameters) {
          const parameterMetadata = this.getParameterMetadata(
            item.ref,
            parameter.ref
          );

          if (parameterMetadata?.calculateValue) {
            parameter.value = parameterMetadata.calculateValue(
              config,
              item,
              parameter.value,
              items
            );
          }
        }

        // if the parameter set is the default parameter set it is neither an
        // approach nor a depart
        if (isDefaultParameterSet) {
          this.setParameterValue(
            item,
            BeaconCuttingData.FlowItemParameterRef.APPROACH,
            false
          );
          this.setParameterValue(
            item,
            BeaconCuttingData.FlowItemParameterRef.DEPART,
            false
          );
        } else if (singleItemCut) {
          this.setParameterValue(
            item,
            BeaconCuttingData.FlowItemParameterRef.APPROACH,
            true
          );
          this.setParameterValue(
            item,
            BeaconCuttingData.FlowItemParameterRef.DEPART,
            true
          );
        } else {
          this.setParameterValue(
            item,
            BeaconCuttingData.FlowItemParameterRef.APPROACH,
            contiguousCuts === 1
          );

          if (contiguousCuts >= 1) {
            // we need to find the next item that is not a section
            let nextItem: FlowItem | undefined;

            for (let i = itemIdx + 1; i < items.length; i++) {
              nextItem = items[i];

              if (nextItem?.ref !== BeaconCuttingData.FlowItemRef.SECTION) {
                break;
              }
            }

            const isNextSingleItemCut = nextItem
              ? this.isSinglePrimaryActionItem(nextItem)
              : false;
            if (
              nextItem === undefined ||
              nextItem?.ref !== BeaconCuttingData.FlowItemRef.CUT ||
              isNextSingleItemCut
            ) {
              this.setParameterValue(
                item,
                BeaconCuttingData.FlowItemParameterRef.DEPART,
                true
              );
            } else {
              this.setParameterValue(
                item,
                BeaconCuttingData.FlowItemParameterRef.DEPART,
                false
              );
            }
          } else {
            this.setParameterValue(
              item,
              BeaconCuttingData.FlowItemParameterRef.DEPART,
              false
            );
          }
        }
      }

      itemIdx++;
    }

    return items;
  }

  public getFineTuneProps(
    flow: Flow<BeaconCuttingData.FlowConfig>,
    item: FlowItem
  ): FineTuneProps {
    let supported = false;

    const itemType = getParameterValue<BeaconCuttingData.CutTypeEnum>(
      item,
      BeaconCuttingData.FlowItemParameterRef.CUT_TYPE
    );

    if (item.ref === BeaconCuttingData.FlowItemRef.CUT) {
      if (![BeaconCuttingData.CutTypeEnum.ROTARY].includes(itemType)) {
        supported = true;
      }
    }

    const contiguousItems = this.getContiguousItemsForFlowItem(
      item,
      flow.items
    );

    const itemIndex = contiguousItems.findIndex((i) => i.id === item.id);
    const executingIndex = flow.items.findIndex((i) => i.id === item?.id);

    let isLast = false;

    if (itemIndex === contiguousItems.length - 1) {
      isLast = true;
    }

    let startItem: FlowItem = item;
    let midItem: FlowItem | undefined;
    let endItem: FlowItem | undefined;
    let radial: boolean = false;

    let pointToProcess = FineTunedPointRequired.START_POINT;

    if (itemType === BeaconCuttingData.CutTypeEnum.RADIAL) {
      radial = true;

      const previous = contiguousItems[itemIndex - 1];
      const next = contiguousItems[itemIndex + 1];

      if (
        previous === undefined ||
        !isCutType(previous, BeaconCuttingData.CutTypeEnum.RADIAL)
      ) {
        startItem = item;
        midItem = next;
        endItem = contiguousItems[itemIndex + 2];
      } else if (next === undefined) {
        endItem = item;
        midItem = previous;
        startItem = contiguousItems[itemIndex - 2];

        pointToProcess = FineTunedPointRequired.END_POINT;
      } else if (
        previous !== undefined &&
        isCutType(previous, BeaconCuttingData.CutTypeEnum.RADIAL)
      ) {
        startItem = previous;
        midItem = item;
        endItem = next;

        pointToProcess = FineTunedPointRequired.MID_POINT;
      }
    } else {
      startItem = item;
      endItem = isLast
        ? contiguousItems[itemIndex - 1]
        : contiguousItems[itemIndex + 1];
    }

    const workPiecePosition =
      item.attributes?.workSurfacePosition ??
      this.getInferredWorkSurfacePosition({
        item,
        flowItems: flow.items ?? [],
      });

    const workPieceOffset = item.attributes?.workSurfaceOffset ?? 0;

    const fineTuneProps: FineTuneProps = {
      supported,
      type: radial ? 'radial' : 'linear',
      pointToProcess: pointToProcess,
      startPoint: getParameterValue(
        startItem,
        BeaconCuttingData.FlowItemParameterRef.WAYPOINT
      ),
      midPoint: midItem
        ? getParameterValue(
            midItem,
            BeaconCuttingData.FlowItemParameterRef.WAYPOINT
          )
        : undefined,
      endPoint: endItem
        ? getParameterValue(
            endItem,
            BeaconCuttingData.FlowItemParameterRef.WAYPOINT
          )
        : undefined,
      toolConfig: flow.config.torch,
      stickout: 0,
      poseIncludesStickout: true,
      workPiecePosition,
      workPieceOffset,
      ui: {
        path: {
          label: 'welding:weld_path',
        },
        stickout: {
          label: 'welding:stickout_offset',
        },
        travelAngle: {
          label: 'welding:travel_angle',
        },
        workAngle: {
          label: 'welding:work_angle',
        },
        applyTo: {
          label: 'welding:fine_tune_apply_to',
        },
      },
      executingIndex: executingIndex,
    };

    return fineTuneProps;
  }

  public getMoveToPointProps(
    flow: Flow<BeaconCuttingData.FlowConfig>,
    item?: FlowItem
  ): MoveToPointProps {
    let moveToPointProps: MoveToPointProps = {};

    const isMoveHome = item?.ref === BeaconCuttingData.FlowItemRef.MOVE_HOME;

    let point: Waypoint | undefined = undefined;
    if (item) {
      point = isMoveHome
        ? flow.config.home.waypoint
        : getParameterValue<Waypoint>(
            item,
            BeaconCuttingData.FlowItemParameterRef.WAYPOINT
          );
    }

    if (point && item) {
      const qnear = isMoveHome
        ? flow.config.home.qnear
        : getParameterValue<QNear>(
            item,
            BeaconCuttingData.FlowItemParameterRef.QNEAR
          );

      // for now we do not want to pass in cut height, but instead go to the
      // exact point that the customer taught

      // const cutHeight = isMoveHome
      //   ? 0
      //   : getParameterValue<number>(
      //       item,
      //       BeaconCuttingData.FlowItemParameterRef.CUT_HEIGHT
      //     );

      const itemIndex = flow.items.findIndex((i) => i.id === item?.id);

      moveToPointProps = {
        point,
        qnear,
        stickout: 0,
        toolConfig: flow.config.torch,
        executingIndex: itemIndex,
      };
    }

    return moveToPointProps;
  }

  public buildFlowItemTextArr(
    flowItems: FlowItemArrayType[]
  ): (FlowItemText | undefined)[] {
    let currentSectionIdx = 0;
    let currentItemIdx = 1;
    let currentAlpha = 'a';
    let prevItem: FlowItem | undefined;

    return flowItems.map<FlowItemText | undefined>((item) => {
      if (instanceOfHelper(item) || !item.id) {
        return undefined;
      }

      let filteredName = item.name?.replace(/ - \d+$/, '') ?? '';

      if (item.ref === BeaconCuttingData.FlowItemRef.SECTION) {
        currentSectionIdx++;

        return {
          itemId: item.id,
          index: `${currentSectionIdx}`,
          name: filteredName,
          label: `${currentSectionIdx} - ${filteredName}`,
        };
      }

      if (item.ref === BeaconCuttingData.FlowItemRef.WAIT_FOR) {
        const type = getParameterValue(
          item,
          BeaconCuttingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT
        );

        if (type === BeaconCuttingData.WaitForInputType.OPERATOR_BUTTON) {
          const message = getParameterValue<string>(
            item,
            BeaconCuttingData.FlowItemParameterRef.WAIT_FOR_OPERATOR_MESSAGE
          );
          const defaultItem = this.getDefaultItemForRef(
            BeaconCuttingData.FlowItemRef.WAIT_FOR
          );
          const defaultName = this.generateFlowItemName(
            BeaconCuttingData.FlowItemRef.WAIT_FOR
          );
          const defaultMessage = getParameterValue<string>(
            defaultItem,
            BeaconCuttingData.FlowItemParameterRef.WAIT_FOR_OPERATOR_MESSAGE
          );
          if (filteredName === defaultName) {
            if (message?.length > 0 && message !== defaultMessage) {
              filteredName = message;
            }
          }
        }
      }

      const isFirst = prevItem === undefined;
      const singleItemCut = this.isSinglePrimaryActionItem(item);

      if (item.ref !== BeaconCuttingData.FlowItemRef.CUT || singleItemCut) {
        // this logic handles non-cuts and single point cuts
        if (!isFirst) {
          ++currentItemIdx;
        }

        prevItem = item;

        return {
          itemId: item.id,
          index: `${currentItemIdx}`,
          name: filteredName,
          label: `${currentItemIdx} - ${filteredName}`,
        };
      } else {
        // this logic handles contiguous cuts
        if (isFirst) {
          prevItem = item;

          return {
            itemId: item.id,
            index: `${currentItemIdx}${currentAlpha}`,
            name: filteredName,
            label: `${currentItemIdx}${currentAlpha} - ${filteredName}`,
          };
        } else {
          // the only non-sequence breaking item is a contiguous cuts
          // at this point, prevItem should always be set
          const prevIsNotSequenceBreaking =
            prevItem?.ref === BeaconCuttingData.FlowItemRef.CUT &&
            !this.isSinglePrimaryActionItem(prevItem);

          if (prevIsNotSequenceBreaking) {
            currentAlpha = getNextChar(currentAlpha);
          } else {
            currentAlpha = 'a';
            ++currentItemIdx;
          }

          prevItem = item;
          return {
            itemId: item.id,
            index: `${currentItemIdx}${currentAlpha}`,
            name: filteredName,
            label: `${currentItemIdx}${currentAlpha} - ${filteredName}`,
          };
        }
      }
    });
  }

  public isShiftableItem(item: FlowItem): boolean {
    if (
      item.ref === BeaconCuttingData.FlowItemRef.MOVE ||
      item.ref === BeaconCuttingData.FlowItemRef.CUT
    ) {
      return true;
    }

    return false;
  }

  public getFlowSelectedItemInfo(
    flow: Flow,
    selectedItems: string[]
  ): FlowSelectedItemInfo {
    const stats: FlowSelectedItemInfo = {
      linear: { count: 0, selected: 0 },
      linearStitch: { count: 0, selected: 0 },
      other: { count: 0, selected: 0 },
      radial: { count: 0, selected: 0 },
      rotary: { count: 0, selected: 0 },
    };

    for (let index = 0; index < flow?.items?.length; index++) {
      const item = flow.items[index];

      if (item.id !== undefined) {
        if (item.ref === BeaconCuttingData.FlowItemRef.CUT) {
          const cutType = getParameterValue<BeaconCuttingData.CutTypeEnum>(
            item,
            BeaconCuttingData.FlowItemParameterRef.CUT_TYPE
          );

          if (cutType === BeaconCuttingData.CutTypeEnum.LINEAR) {
            stats.linear = incrementItemStats(
              stats.linear,
              selectedItems.includes(item.id)
            );
          } else if (cutType === BeaconCuttingData.CutTypeEnum.RADIAL) {
            stats.radial = incrementItemStats(
              stats.radial,
              selectedItems.includes(item.id)
            );
          } else if (cutType === BeaconCuttingData.CutTypeEnum.ROTARY) {
            stats.rotary = incrementItemStats(
              stats.rotary,
              selectedItems.includes(item.id)
            );
          }
        } else {
          stats.other = incrementItemStats(
            stats.other,
            selectedItems.includes(item.id)
          );
        }
      }
    }

    return stats;
  }

  public getFlowItemSubheading(
    flowItem: FlowItem<BeaconCuttingData.FlowItemRef>,
    system?: SystemUnit
  ): string | undefined {
    let subheading: string | undefined;

    const metadata = this.getItemMetadata(flowItem.ref);

    if (metadata) {
      if (flowItem.ref === BeaconCuttingData.FlowItemRef.CUT) {
        subheading = this.getCutItemSubheading(flowItem, system);
      } else if (
        flowItem.ref === BeaconCuttingData.FlowItemRef.MOVE ||
        flowItem.ref === BeaconCuttingData.FlowItemRef.MOVE_HOME
      ) {
        subheading = getLocalizedText(metadata.name);
      } else if (flowItem.ref === BeaconCuttingData.FlowItemRef.WAIT_FOR) {
        subheading = this.getWaitForItemSubheading(flowItem);
      } else if (
        flowItem.ref === BeaconCuttingData.FlowItemRef.PRODUCTION_REPORTING
      ) {
        subheading = this.getProductionItemSubheading(flowItem);
      } else if (
        flowItem.ref === BeaconCuttingData.FlowItemRef.TRIGGER_OUTPUT
      ) {
        subheading = this.getTriggerOutputItemSubheading(flowItem);
      }
    }

    return subheading;
  }

  public getApplicationDefaultParameterSetImplicitTags(): Record<
    string,
    unknown
  > {
    // TODO
    return {};
  }

  public getContiguousItemsForFlowItem(
    item: FlowItem,
    items: FlowItem[]
  ): FlowItem[] {
    if (item.ref === BeaconCuttingData.FlowItemRef.CUT) {
      const start = this.getStartOfSequenceIndexFromFlowItem(item, items);
      const end = this.getEndOfSequenceIndexFromFlowItem(item, items);
      if (start !== undefined && end !== undefined) {
        return items.slice(start, end + 1);
      }
    }

    return [];
  }

  public getStartOfSequenceIndexFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): number | undefined {
    let startIndex: number | undefined = undefined;
    const flowItemIndex: number | undefined = flowItem.id
      ? flowItems.findIndex((i) => i.id !== undefined && i.id === flowItem.id)
      : undefined;

    const isCutItem = flowItem.ref === BeaconCuttingData.FlowItemRef.CUT;

    if (flowItemIndex !== undefined && isCutItem) {
      if (this.hasApproach(flowItem)) {
        startIndex = flowItemIndex;
      } else {
        for (let i = flowItemIndex; i >= 0; i--) {
          const item = flowItems[i];
          if (
            !this.isOfType(FlowItemSharedRef.SECTION, item) &&
            isCutItem &&
            this.hasApproach(item)
          ) {
            startIndex = i;
            break;
          }
        }
      }
    }
    return startIndex;
  }

  public getEndOfSequenceIndexFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): number | undefined {
    let endIndex: number | undefined = undefined;
    const flowItemIndex: number | undefined = flowItem.id
      ? flowItems.findIndex((i) => i.id !== undefined && i.id === flowItem.id)
      : undefined;

    const isCutItem = flowItem.ref === BeaconCuttingData.FlowItemRef.CUT;

    if (flowItemIndex !== undefined && isCutItem) {
      if (this.hasDepart(flowItem)) {
        endIndex = flowItemIndex;
      } else {
        for (let i = flowItemIndex; i <= flowItems.length; i++) {
          const item = flowItems[i];
          if (
            !this.isOfType(FlowItemSharedRef.SECTION, item) &&
            isCutItem &&
            this.hasDepart(item)
          ) {
            endIndex = i;
            break;
          }
        }
      }
    }
    return endIndex;
  }

  public getEndOfSequenceItemFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): FlowItem | undefined {
    const endIndex = this.getEndOfSequenceIndexFromFlowItem(
      flowItem,
      flowItems
    );
    return endIndex !== undefined ? flowItems[endIndex] : undefined;
  }

  public isSinglePrimaryActionItem(item: FlowItem): boolean {
    if (item.ref === this.getDefaultItemRef()) {
      const singleItemCuts = [BeaconCuttingData.CutTypeEnum.ROTARY];

      const cutType = getParameterValue<BeaconCuttingData.CutTypeEnum>(
        item,
        BeaconCuttingData.FlowItemParameterRef.CUT_TYPE
      );

      return singleItemCuts.includes(cutType);
    }

    return false;
  }

  public getCopyWithNestedItems(items: FlowItem[]): FlowItem[] {
    // Cutting service doesn't use nested items, so just return a deep clone
    return cloneDeep(items);
  }

  protected getProductionItemSubheading(flowItem: FlowItem): string {
    const qty = getParameterValue(
      flowItem,
      BeaconCuttingData.FlowItemParameterRef.PRODUCTION_QUANTITY
    );

    const subheading = getLocalizedText(
      BeaconCuttingData.BeaconReportProductionItem.name
    ) as string;

    return `${subheading} (${getLocalizedText('common:quantity_abbr')} ${qty})`;
  }

  protected hasApproach(item: FlowItem): boolean {
    const parameter: FlowItemParameter<boolean> | undefined =
      item.parameters.find(
        (p) => p.ref === BeaconCuttingData.FlowItemParameterRef.APPROACH
      );
    if (parameter) {
      return parameter.value;
    }

    return false;
  }

  protected hasDepart(item: FlowItem): boolean {
    const parameter: FlowItemParameter<boolean> | undefined =
      item.parameters.find(
        (p) => p.ref === BeaconCuttingData.FlowItemParameterRef.DEPART
      );
    if (parameter) {
      return parameter.value;
    }

    return false;
  }

  protected getCutItemSubheading(
    flowItem: FlowItem,
    system?: SystemUnit
  ): string {
    const subheadingParts: string[] = [];

    const isApproach = this.hasApproach(flowItem);
    const isDepart = this.hasDepart(flowItem);
    const isSingleItemCut = this.isSinglePrimaryActionItem(flowItem);

    if (isDepart && !isSingleItemCut) {
      return `${getLocalizedText('cutting:end_of_cut')}`;
    }

    const cutTypeText = this.getFlowItemPickerLabel(
      flowItem,
      BeaconCuttingData.FlowItemParameterRef.CUT_TYPE
    );

    const processOption = this.getFlowItemPickerOption(
      flowItem,
      BeaconCuttingData.FlowItemParameterRef.PROCESS
    );

    const processSettings: string[] = [];

    if (cutTypeText) {
      subheadingParts.push(cutTypeText);
    }

    const travel = getParameterValue<number>(
      flowItem,
      BeaconCuttingData.FlowItemParameterRef.TRAVEL_SPEED
    );

    if (travel) {
      let travelSpeedText: string;
      if (system) {
        const convertedTravelSpeed = convertParameterValueToUOM(
          BeaconCuttingData.TravelSpeedMetadata.value,
          system,
          Number(travel),
          false
        );
        travelSpeedText = `${convertedTravelSpeed} ${
          system === SystemUnit.IMPERIAL ? 'ipm' : 'cpm'
        }`;
      } else {
        travelSpeedText = `${travel}`;
      }

      processSettings.push(travelSpeedText);
    }

    if (isApproach) {
      const pierceHeight = getParameterValue<number>(
        flowItem,
        BeaconCuttingData.FlowItemParameterRef.PIERCE_HEIGHT
      );

      const pierceDelay = getParameterValue<number>(
        flowItem,
        BeaconCuttingData.FlowItemParameterRef.PIERCE_DELAY
      );

      processSettings.push(` ${pierceHeight}`);
      processSettings.push(` ${pierceDelay}s`);
    }

    if (!isApproach) {
      const cutHeight = getParameterValue<number>(
        flowItem,
        BeaconCuttingData.FlowItemParameterRef.CUT_HEIGHT
      );

      processSettings.push(` ${cutHeight} ch`);
    }

    if (processOption) {
      const label = getLocalizedText(processOption.label as i18nKeyType);
      subheadingParts.push(`${label} (${processSettings})`);
    }

    return subheadingParts.join(', ');
  }

  protected getWaitForItemSubheading(flowItem: FlowItem): string {
    const waitForLabel = getLocalizedText(
      BeaconCuttingData.BeaconWaitForItem.name
    ) as string;

    const type = getParameterValue(
      flowItem,
      BeaconCuttingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT
    );

    const typeOption: PickerOption | undefined =
      BeaconCuttingData.WaitForTypeMetadata.ui?.widgetOptions.find(
        (o: PickerOption) => {
          return o.value === type;
        }
      );

    const typeLabel = getLocalizedText(
      typeOption?.label as i18nKeyType
    )?.toLowerCase();

    if (type === BeaconCuttingData.WaitForInputType.SENSOR) {
      const inputLabel = this.getFlowItemPickerLabel(
        flowItem,
        BeaconCuttingData.FlowItemParameterRef.WAIT_FOR_INPUT
      )?.toLowerCase();

      const stateLabel = this.getFlowItemPickerLabel(
        flowItem,
        BeaconCuttingData.FlowItemParameterRef.WAIT_FOR_INPUT_STATE
      )?.toLowerCase();

      return `${waitForLabel} ${inputLabel} - ${stateLabel}`;
    } else if (type === BeaconCuttingData.WaitForInputType.TIME) {
      const time = getParameterValue(
        flowItem,
        BeaconCuttingData.FlowItemParameterRef.WAIT_FOR_TIME
      );
      return `${waitForLabel} ${time}s`;
    } else if (
      type === BeaconCuttingData.WaitForInputType.OPERATOR_BUTTON ||
      typeLabel === undefined
    ) {
      let suffix = typeLabel;

      // backwards compatibility for wait for operator label where we don't have a type
      if (typeLabel === undefined) {
        const option = this.getFlowItemPickerOptionByValue(
          flowItem,
          BeaconCuttingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT,
          BeaconCuttingData.WaitForInputType.OPERATOR_BUTTON
        );

        if (option) {
          suffix = getLocalizedText(option.label as i18nKeyType)?.toLowerCase();
        }
      }

      return `${waitForLabel} ${suffix}`;
    }

    return waitForLabel;
  }

  protected getTriggerOutputItemSubheading(flowItem: FlowItem): string {
    const triggerText = getLocalizedText('common:trigger');
    const outputText = getLocalizedText('common:output')?.toLowerCase();

    const outputLabel = this.getFlowItemPickerLabel(
      flowItem,
      BeaconCuttingData.FlowItemParameterRef.TRIGGER_OUTPUT_PORT
    )?.toLowerCase();

    const stateLabel = this.getFlowItemPickerLabel(
      flowItem,
      BeaconCuttingData.FlowItemParameterRef.TRIGGER_OUTPUT_STATE
    )?.toLowerCase();

    return `${triggerText} ${outputLabel} ${outputText} - ${stateLabel}`;
  }
}

export { CuttingFlowItemServiceImpl };
